import React, { useState, useEffect } from 'react';
import tw from 'tailwind-styled-components';
import { AiOutlineClose } from 'react-icons/ai';
import { FaSearch, FaMapMarkerAlt } from 'react-icons/fa';
import { MdMyLocation } from 'react-icons/md';
import axios from 'axios';

const LocationModal = ({ setOpenModal, onEnableLocation, isLoading = false, currentLocation, onClearLocation, onLocationSelect, error, clearError }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);

    // Search for locations using OpenStreetMap Nominatim API
    const searchLocations = async (query) => {
        if (!query.trim()) {
            setSearchResults([]);
            setHasSearched(false);
            return;
        }

        setIsSearching(true);
        try {
            const response = await axios.get(
                `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5&addressdetails=1`
            );

            const results = response.data.map(result => ({
                id: result.place_id,
                display_name: result.display_name,
                lat: result.lat,
                lon: result.lon,
                address: result.address,
                formatted_address: formatAddress(result.address)
            }));

            setSearchResults(results);
            setHasSearched(true);
        } catch (error) {
            console.error('Error searching locations:', error);
            setSearchResults([]);
            setHasSearched(true);
        } finally {
            setIsSearching(false);
        }
    };

    // Format address for display
    const formatAddress = (address) => {
        if (!address) return '';

        const parts = [];
        if (address.house_number && address.road) {
            parts.push(`${address.house_number} ${address.road}`);
        } else if (address.road) {
            parts.push(address.road);
        }

        if (address.city || address.town || address.village) {
            parts.push(address.city || address.town || address.village);
        }

        if (address.state) {
            parts.push(address.state);
        }

        if (address.country) {
            parts.push(address.country);
        }

        return parts.join(', ');
    };

    // Handle search input change
    useEffect(() => {
        const delayedSearch = setTimeout(() => {
            if (searchQuery.length > 2) {
                searchLocations(searchQuery);
            } else {
                setSearchResults([]);
                setHasSearched(false);
            }
        }, 500);

        return () => clearTimeout(delayedSearch);
    }, [searchQuery]);

    // useEffect(() => {
    //     if (error) {
    //         const timer = setTimeout(() => {
    //             clearError();
    //         }, 5000); // Clear the error after 5 seconds

    //         return () => clearTimeout(timer); // Cleanup timer on component unmount
    //     }
    // }, [error, clearError]);

    // Handle location selection from search results
    const handleLocationSelect = (location) => {
        const locationString = location.formatted_address || location.display_name;
        localStorage.setItem('userLocation', locationString);

        if (onLocationSelect) {
            onLocationSelect(locationString);
        } else {
            window.location.reload();
        }

        setOpenModal(false);
    };

    return (
        <ModalWrapper onClick={() => setOpenModal(false)}>
            <ModalBox onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between p-4 border-b">
                    <ModalTitle>Your Location</ModalTitle>
                    <AiOutlineClose
                        onClick={() => setOpenModal(false)}
                        className="text-gray-500 cursor-pointer hover:text-gray-900"
                    />
                </div>

                {/* Search Bar */}
                <div className="p-4">
                    <div className="relative flex items-center">
                        <FaSearch className="absolute left-3 w-5 h-5 text-kuality-blue" />
                        <input
                            type="text"
                            placeholder="Search a new address"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 bg-gray-100 rounded-md text-md focus:outline-none"
                        />
                        {isSearching && (
                            <div className="absolute right-3">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
                            </div>
                        )}
                    </div>
                </div>

                {error && (
                    <ErrorMessage>{error}</ErrorMessage>
                )}

                {/* Search Results */}
                {searchResults.length > 0 && (
                    <div className="px-4 pb-4 max-h-48 overflow-y-auto">
                        <div className="space-y-2">
                            {searchResults.map((location) => (
                                <div
                                    key={location.id}
                                    onClick={() => handleLocationSelect(location)}
                                    className="flex items-center space-x-3 p-3 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors"
                                >
                                    <FaMapMarkerAlt className="text-kuality-blue w-4 h-4 flex-shrink-0" />
                                    <div className="flex flex-col flex-1 min-w-0">
                                        <span className="text-gray-800 text-sm font-medium truncate">
                                            {location.formatted_address || location.display_name}
                                        </span>
                                        {location.formatted_address && location.display_name !== location.formatted_address && (
                                            <span className="text-gray-500 text-xs truncate">
                                                {location.display_name}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {hasSearched && searchResults.length === 0 && searchQuery.length > 2 && !isSearching && (
                    <div className="px-4 pb-4">
                        <div className="text-center text-gray-500 text-sm py-4">
                            No locations found for "{searchQuery}"
                        </div>
                    </div>
                )}

                <div className="p-4">
                    {currentLocation ? (
                        <div className="mb-2 p-2 ">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-3">
                                    <FaMapMarkerAlt className=" w-5 h-5" />
                                    <div className="flex flex-col">
                                        <span className="text-gray-800 text-sm">{currentLocation}</span>
                                    </div>
                                </div>
                                <button
                                    onClick={onClearLocation}
                                    className="bg-white  font-semibold text-xs px-3 py-1 rounded-md shadow hover:bg-gray-50"
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    ) : null}

                    <div className="flex items-center justify-between p-4 bg-kuality-bg rounded-lg border-2 border-kuality-blue">
                        <div className="flex items-center space-x-3">
                            <MdMyLocation className="text-kuality-blue w-5 h-5" />
                            <div className="flex flex-col">
                                <span className="font-semibold text-sm">Use My Current Location</span>
                                <span className="text-gray-500 text-xs">Enable your current location for better services</span>
                            </div>
                        </div>
                        <button
                            onClick={onEnableLocation}
                            disabled={isLoading || error === "Your browser does not support geolocation."}
                            className={`${isLoading || error === "Your browser does not support geolocation."
                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                : 'bg-white text-kuality-blue hover:bg-gray-50'
                                } font-semibold text-xs px-4 py-2 rounded-md shadow transition-colors`}
                        >
                            {isLoading ? 'Enabling...' : 'Enable'}
                        </button>
                    </div>
                </div>

                <div className="flex justify-center p-4">
                </div>
            </ModalBox>
        </ModalWrapper>
    );
};

const ModalWrapper = tw.div`
fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-50
`;

const ModalBox = tw.div`
bg-white rounded-lg shadow-xl w-11/12 max-w-sm md:max-w-md relative
`;

const ModalTitle = tw.h2`
text-xl font-bold text-gray-800
`;

const ErrorMessage = tw.div`
  text-center p-1 text-red-700 text-sm font-medium 
`;
export default LocationModal;